import { Component } from 'react';
import type { ErrorInfo, ReactNode } from 'react';
import { AlertCircle, RefreshCw } from 'lucide-react';
import * as Sentry from '@sentry/react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  /** Compact inline card — use for per-feature boundaries inside a page */
  compact?: boolean;
  /** Label shown in compact error card (e.g. "Analitik") */
  label?: string;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error?: Error;
  sentryId?: string;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught:', error, errorInfo);

    let sentryId: string | undefined;
    if (import.meta.env.PROD) {
      sentryId = Sentry.captureException(error, {
        contexts: { react: { componentStack: errorInfo.componentStack } },
        level: 'error',
        tags: { errorBoundary: true, label: this.props.label ?? 'unknown' },
      });
    }

    this.setState({ sentryId });
    this.props.onError?.(error, errorInfo);
  }

  handleReset = () => {
    this.setState({ hasError: false, error: undefined, sentryId: undefined });
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    if (this.props.fallback) return this.props.fallback;

    // ── Compact inline card (per-feature) ─────────────────────────
    if (this.props.compact) {
      return (
        <div
          className="rounded-xl border border-red-500/20 bg-red-500/5 p-4 flex flex-col gap-3"
          role="alert"
          aria-live="assertive"
        >
          <div className="flex items-center gap-2">
            <AlertCircle className="w-4 h-4 text-red-400 shrink-0" />
            <span className="text-sm font-semibold text-red-300">
              {this.props.label ? `${this.props.label} yüklenemedi` : 'Bileşen hatası'}
            </span>
          </div>
          {this.state.error && (
            <p className="text-xs font-mono text-red-400/80 break-all">
              {this.state.error.message}
            </p>
          )}
          <div className="flex items-center gap-3">
            <button
              onClick={this.handleReset}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-500/15 hover:bg-red-500/25 text-red-300 text-xs font-semibold transition-colors"
            >
              <RefreshCw className="w-3 h-3" />
              Tekrar Dene
            </button>
            {this.state.sentryId && (
              <span className="text-[10px] font-mono text-red-500/50">
                ID: {this.state.sentryId.slice(0, 8)}
              </span>
            )}
          </div>
        </div>
      );
    }

    // ── Full-page fallback (global boundary) ──────────────────────
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ background: 'var(--bg-1)' }}>
        <div className="max-w-md w-full rounded-xl border border-red-500/20 bg-red-500/5 p-6 space-y-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-red-500/10">
              <AlertCircle className="w-6 h-6 text-red-400" />
            </div>
            <h2 className="text-lg font-bold text-white">Beklenmedik Hata</h2>
          </div>

          <p className="text-sm text-gray-400">
            Uygulama beklenmedik bir hatayla karşılaştı. Lütfen tekrar deneyin; sorun devam ederse sayfayı yenileyin.
          </p>

          {this.state.error && (
            <div className="rounded-lg p-3 text-xs font-mono text-red-400 break-all" style={{ background: 'rgba(15,17,21,0.6)', border: '1px solid rgba(252,165,165,0.15)' }}>
              {this.state.error.message}
            </div>
          )}

          <div className="flex items-center gap-3">
            <button
              onClick={this.handleReset}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-300 text-sm font-semibold transition-colors"
            >
              <RefreshCw className="w-4 h-4" />
              Tekrar Dene
            </button>
            <button
              onClick={() => window.location.reload()}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white/5 hover:bg-white/10 text-gray-400 text-sm font-semibold transition-colors"
            >
              Sayfayı Yenile
            </button>
          </div>

          {this.state.sentryId && (
            <p className="text-[10px] font-mono text-gray-600">
              Hata ID: {this.state.sentryId}
            </p>
          )}
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
